import {
  ClockIcon,
  CollectionIcon,
  PhotographIcon,
  PlayIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  RefreshIcon,
  SparklesIcon,
  XIcon,
} from '@heroicons/react/outline';
import { PauseIcon, TrashIcon } from '@heroicons/react/solid';
import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import useLocale from 'next-translate/useTranslation';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';
import {
  describeEditRange,
  formatEditSeconds,
  MAX_EDIT_INSTRUCTION_LENGTH,
  MAX_EDIT_INSTRUCTIONS,
  MAX_EDIT_REFERENCE_IMAGES,
  normalizeVideoEditInstructions,
  parseComposedVideoEditPrompt,
  VideoEditInstruction,
} from '@/lib/videoEditPrompt';

import ResolutionSelector from '@/components/images/ResolutionSelector';
import ButtonLink from '@/components/links/ButtonLink';
import { SimpleTooltip } from '@/components/ui/tooltip';

import {
  getSeedance25EditCredits,
  getVideoEditTierLimits,
  SEEDANCE_25_EDIT_MAX_SECONDS,
  SEEDANCE_25_EDIT_MIN_SECONDS,
  SEEDANCE_25_EDIT_RESOLUTIONS,
  Seedance25EditTier,
  videoModelsWithDescription,
} from '@/constants/videoModels';
import { compressImage } from '@/utils/compressImage';
import axiosInstance from '@/utils/httpClient';
import { toastError } from '@/utils/toastError';
import { uploadUserImage } from '@/utils/uploadUserImage';
import { useUser } from '@/utils/useUser';

import Button from '../buttons/Button';
import Spinner from '../Spinner';

// The media library (uploads, characters, objects, generated images). Loaded
// on first open: the gallery mounts this editor on every video page.
const SceneModal = dynamic(() => import('@/components/images/SceneModal'), {
  ssr: false,
});

/** SceneModal's library buckets; a reference keeps the one it was picked from. */
type ReferenceBucket = 'character' | 'object' | 'image';

/** The library opens without its scene tabs, which mean nothing to an edit. */
const LIBRARY_ONLY_TABS = ['style', 'camera', 'lighting', 'shot-type'] as const;

type EditResolution = '480p' | '720p' | '1080p';

interface Cue extends VideoEditInstruction {
  id: string;
}

interface VideoEditModalProps {
  /**
   * The user's own gallery row, or null for a clip uploaded into the Edit
   * Video tool — the route then validates the URL's host instead of row
   * ownership.
   */
  videoId: number | null;
  /** Empty in the Edit Video tool until a clip is picked — the form is shown
   *  anyway, with `sourcePicker` in the player's place. */
  videoUrl: string;
  /** Cues to start from — set when re-opening the editor on a previous edit. */
  initialInstructions?: VideoEditInstruction[];
  /** The previous edit's reference images, engine and resolution, likewise. */
  initialReferenceImages?: string[];
  initialTier?: Seedance25EditTier;
  initialResolution?: string;
  /**
   * 'modal' (default) is the gallery's "Edit with AI" dialog: header, two
   * columns, Cancel. 'inline' drops those for the narrow tool panel on
   * /ai-videos?tab=edit-video, which supplies its own heading and source
   * picker.
   */
  variant?: 'modal' | 'inline';
  /** Inline only: what to show in the player's place while there is no clip. */
  sourcePicker?: React.ReactNode;
  /** Inline only: swap the clip (the tool opens its file picker). */
  onReplaceVideo?: () => void;
  /** Inline only: "Clear inputs" also drops the clip. */
  onClearVideo?: () => void;
  onClose?: () => void;
  /** Fired with the pending row once the edit has been submitted. */
  onSubmitted?: (video: any) => void;
}

/**
 * One-tap starting points. Every template opens with a word from ModelArk's
 * editing-trigger list (add / remove / replace / change), which is what makes
 * Seedance 2.5 classify the request as an edit instead of a fresh
 * reference-to-video render.
 */
const QUICK_EDITS: { label: string; template: string; global?: boolean }[] = [
  { label: 'Remove an object', template: 'remove the ' },
  { label: 'Replace a subject', template: 'replace the  with ' },
  { label: 'Change the background', template: 'change the background to ' },
  { label: 'Change the lighting', template: 'change the lighting to ' },
  { label: 'Change the outfit', template: "change the person's outfit to " },
  {
    label: 'Remove on-screen text',
    template: 'remove the on-screen text, subtitles, and watermarks',
    global: true,
  },
  {
    label: 'Remove the music',
    template: 'remove the background music and keep the other sounds',
    global: true,
  },
  {
    label: 'Change the style',
    template: 'change the visual style to ',
    global: true,
  },
];

/** The editing engines, shown as the "Model" choice like the generator's. */
const EDIT_ENGINES: {
  tier: Seedance25EditTier;
  label: string;
  /** Catalogue entry whose icon the row borrows. */
  catalogTitle: string;
  blurb: string;
}[] = [
  {
    tier: 'standard',
    label: 'Seedance 2.5',
    catalogTitle: 'Seedance 2.5',
    blurb: 'Best quality',
  },
  {
    tier: 'turbo',
    label: 'Seedance 2.5 Fast',
    catalogTitle: 'Seedance 2.5 Turbo',
    blurb: 'Faster, about half the price',
  },
  {
    tier: 'wan-3.0',
    label: 'WAN 3.0',
    catalogTitle: 'WAN 3.0',
    blurb: 'Another model family — try it when Seedance refuses a clip',
  },
  {
    tier: 'kling-4.0',
    label: 'Kling 4.0',
    catalogTitle: 'Kling 4.0',
    blurb: 'Keeps the clip’s own sound and dialogue',
  },
];

const engineIcon = (catalogTitle: string) =>
  videoModelsWithDescription.find((model) => model.title === catalogTitle)
    ?.icon;

const fitsTier = (duration: number, tier: Seedance25EditTier) => {
  const { minSeconds, maxSeconds } = getVideoEditTierLimits(tier);
  return duration >= minSeconds - 0.5 && duration <= maxSeconds + 0.5;
};

/** Section heading in the generator's style: label, (?) hint, right slot. */
const SectionLabel = ({
  label,
  hint,
  aside,
}: {
  label: React.ReactNode;
  hint?: string;
  aside?: React.ReactNode;
}) => (
  <div className='mb-3 flex items-center justify-between gap-3'>
    <div className='flex items-center gap-1.5'>
      <span className='text-sm font-medium text-gray-700 dark:text-slate-400'>
        {label}
      </span>
      {hint && (
        <SimpleTooltip title={hint}>
          <QuestionMarkCircleIcon className='h-4 w-4 cursor-help text-gray-400' />
        </SimpleTooltip>
      )}
    </div>
    {aside}
  </div>
);

const newId = () => Math.random().toString(36).slice(2, 10);

/** Tick spacing that keeps the ruler readable at any clip length. */
const tickStep = (duration: number) => {
  if (duration <= 8) return 1;
  if (duration <= 16) return 2;
  if (duration <= 40) return 5;
  return 10;
};

const clampSecond = (value: number, min: number, max: number) =>
  Math.min(Math.max(Math.round(value), min), max);

export default function VideoEditModal({
  videoId,
  videoUrl,
  initialInstructions,
  initialReferenceImages,
  initialTier,
  initialResolution,
  variant = 'modal',
  sourcePicker,
  onReplaceVideo,
  onClearVideo,
  onClose,
  onSubmitted,
}: VideoEditModalProps) {
  const { t } = useLocale('ai-images');
  const { isFreePlan } = useUser();
  const inline = variant === 'inline';

  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  // Held in a ref as well as state so the pointer-move handler, which is
  // registered once per drag, always reads the live duration.
  const durationRef = useRef(0);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [metadataError, setMetadataError] = useState(false);
  const startTier: Seedance25EditTier =
    initialTier && initialTier in SEEDANCE_25_EDIT_RESOLUTIONS
      ? initialTier
      : 'standard';
  const [resolution, setResolution] = useState<EditResolution>(() =>
    initialResolution &&
    (SEEDANCE_25_EDIT_RESOLUTIONS[startTier] as string[]).includes(
      initialResolution,
    )
      ? (initialResolution as EditResolution)
      : '720p',
  );
  const [tier, setTier] = useState<Seedance25EditTier>(startTier);
  const [referenceImages, setReferenceImages] = useState<string[]>(() =>
    (initialReferenceImages ?? []).slice(0, MAX_EDIT_REFERENCE_IMAGES),
  );
  // Which library bucket each reference came from, so reopening the library
  // shows it selected on the right tab. Direct uploads are plain images.
  const [referenceBuckets, setReferenceBuckets] = useState<
    Record<string, ReferenceBucket>
  >({});
  const [uploadingReference, setUploadingReference] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  // Keep SceneModal mounted after the first open so it can animate out.
  const [libraryMounted, setLibraryMounted] = useState(false);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);

  const [cues, setCues] = useState<Cue[]>(() =>
    (initialInstructions ?? []).length
      ? (initialInstructions as VideoEditInstruction[]).map((instruction) => ({
          ...instruction,
          id: newId(),
        }))
      : [{ id: newId(), startSeconds: null, endSeconds: null, text: '' }],
  );
  const [activeCueId, setActiveCueId] = useState<string>(() => cues[0].id);

  const activeCue = cues.find((cue) => cue.id === activeCueId) ?? cues[0];

  const wholeSeconds = Math.max(1, Math.round(duration));
  // The widest window any engine takes decides whether the clip can be edited
  // at all; the selected engine's own window only gates the submit, so a 20s
  // clip on WAN 3.0 shows a "switch engine" note instead of a dead end.
  const tooShort =
    duration > 0 && duration < SEEDANCE_25_EDIT_MIN_SECONDS - 0.5;
  const tooLong = duration > SEEDANCE_25_EDIT_MAX_SECONDS + 0.5;
  const unsupported = tooShort || tooLong || metadataError;
  const tierLimits = getVideoEditTierLimits(tier);
  const tooLongForTier = duration > 0 && duration > tierLimits.maxSeconds + 0.5;
  const tooShortForTier =
    duration > 0 && duration < tierLimits.minSeconds - 0.5;

  const updateCue = useCallback((id: string, patch: Partial<Cue>) => {
    setCues((prev) =>
      prev.map((cue) => (cue.id === id ? { ...cue, ...patch } : cue)),
    );
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, seconds);
    setCurrentTime(Math.max(0, seconds));
  }, []);

  // ----- player -----
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
      setMetadataError(true);
      return;
    }
    durationRef.current = video.duration;
    setDuration(video.duration);
    setMetadataError(false);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  };

  // ----- timeline -----
  const timeFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    const total = durationRef.current;
    if (!track || !total) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = (clientX - rect.left) / Math.max(1, rect.width);
    return Math.min(Math.max(ratio, 0), 1) * total;
  }, []);

  const handleTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (dragging) return;
    seekTo(timeFromClientX(event.clientX));
  };

  const startHandleDrag = (
    event: React.PointerEvent,
    cueId: string,
    edge: 'start' | 'end',
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveCueId(cueId);
    setDragging(edge);

    const onMove = (moveEvent: PointerEvent) => {
      const seconds = timeFromClientX(moveEvent.clientX);
      const max = Math.max(1, Math.round(durationRef.current));
      setCues((prev) =>
        prev.map((cue) => {
          if (cue.id !== cueId) return cue;
          const start = cue.startSeconds ?? 0;
          const end = cue.endSeconds ?? max;
          // Boundaries snap to whole seconds because that is the finest
          // granularity Seedance 2.5 reads from a prompt.
          return edge === 'start'
            ? { ...cue, startSeconds: clampSecond(seconds, 0, end - 1) }
            : { ...cue, endSeconds: clampSecond(seconds, start + 1, max) };
        }),
      );
      // Scrubbing the handle scrubs the picture too, so the boundary can be
      // placed against what is actually on screen.
      seekTo(Math.round(seconds));
    };

    const onUp = () => {
      setDragging(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ----- cues -----
  const addCue = (options: { global?: boolean; text?: string } = {}) => {
    if (cues.length >= MAX_EDIT_INSTRUCTIONS) {
      toast.error(
        t('You can apply up to {{count}} edits at once').replace(
          '{{count}}',
          String(MAX_EDIT_INSTRUCTIONS),
        ),
      );
      return;
    }
    const start = clampSecond(currentTime, 0, Math.max(0, wholeSeconds - 1));
    const cue: Cue = options.global
      ? { id: newId(), startSeconds: null, endSeconds: null, text: '' }
      : {
          id: newId(),
          startSeconds: start,
          endSeconds: clampSecond(start + 2, start + 1, wholeSeconds),
          text: '',
        };
    if (options.text) cue.text = options.text;
    setCues((prev) => [...prev, cue]);
    setActiveCueId(cue.id);
  };

  const removeCue = (id: string) => {
    setCues((prev) => {
      const next = prev.filter((cue) => cue.id !== id);
      if (next.length === 0) {
        const fresh: Cue = {
          id: newId(),
          startSeconds: null,
          endSeconds: null,
          text: '',
        };
        setActiveCueId(fresh.id);
        return [fresh];
      }
      if (id === activeCueId) setActiveCueId(next[0].id);
      return next;
    });
  };

  const applyQuickEdit = (quick: (typeof QUICK_EDITS)[number]) => {
    const target = activeCue;
    // An untouched cue is filled in place; anything already written keeps its
    // text and the template starts a new cue.
    if (target && !target.text.trim()) {
      updateCue(target.id, {
        text: quick.template,
        ...(quick.global ? { startSeconds: null, endSeconds: null } : {}),
      });
      setActiveCueId(target.id);
    } else {
      addCue({ global: quick.global, text: quick.template });
    }
  };

  // A prompt copied from a finished edit's details is our composed text
  // (lead-in, reference line, one line per cue). Pasted as is, the lead-in
  // would be sent twice and the cue cap would cut the real instruction short,
  // so it is split back into the cues it came from.
  const handleCuePaste = (
    cue: Cue,
    event: React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    const parsed = parseComposedVideoEditPrompt(
      event.clipboardData.getData('text'),
    );
    if (!parsed) return;
    event.preventDefault();
    if (parsed.length === 0) return;

    const toCue = (instruction: VideoEditInstruction): Cue => ({
      id: newId(),
      ...instruction,
      text: instruction.text.slice(0, MAX_EDIT_INSTRUCTION_LENGTH),
    });
    setCues((prev) => {
      const index = prev.findIndex((item) => item.id === cue.id);
      if (index === -1) return prev;
      const [first, ...rest] = parsed;
      const replaced: Cue[] = prev[index].text.trim()
        ? [prev[index], toCue(first)]
        : [
            {
              ...prev[index],
              text: first.text.slice(0, MAX_EDIT_INSTRUCTION_LENGTH),
              ...(first.startSeconds !== null
                ? {
                    startSeconds: first.startSeconds,
                    endSeconds: first.endSeconds,
                  }
                : {}),
            },
          ];
      return [
        ...prev.slice(0, index),
        ...replaced,
        ...rest.map(toCue),
        ...prev.slice(index + 1),
      ].slice(0, MAX_EDIT_INSTRUCTIONS);
    });
  };

  const toggleCueScope = (cue: Cue) => {
    if (cue.startSeconds === null) {
      const start = clampSecond(currentTime, 0, Math.max(0, wholeSeconds - 1));
      updateCue(cue.id, {
        startSeconds: start,
        endSeconds: clampSecond(start + 2, start + 1, wholeSeconds),
      });
    } else {
      updateCue(cue.id, { startSeconds: null, endSeconds: null });
    }
  };

  // ----- reference images -----
  const handleReferenceUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_EDIT_REFERENCE_IMAGES - referenceImages.length;
    if (room <= 0) return;
    setUploadingReference(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files)
          .slice(0, room)
          .map(async (file) =>
            uploadUserImage(
              await compressImage(file, { force: true, format: 'jpeg' }),
              'image',
            ),
          ),
      );
      setReferenceImages((prev) => [
        ...prev,
        ...uploaded.map((upload) => upload.image_url),
      ]);
    } catch (error) {
      toastError(error);
    } finally {
      setUploadingReference(false);
    }
  };

  const openLibrary = () => {
    setLibraryMounted(true);
    setShowLibrary(true);
  };

  const referencesInBucket = (bucket: ReferenceBucket) =>
    referenceImages.filter(
      (url) => (referenceBuckets[url] ?? 'image') === bucket,
    );

  // The library returns the whole selection, current references included
  // (they open pre-selected). Existing ones keep their position — edits refer
  // to them as @Image1..4 by index — and new picks are appended up to the cap.
  const applyLibrarySelection = ({
    characters,
    objects,
    images,
  }: {
    characters: string[];
    objects: string[];
    images: string[];
  }) => {
    const wanted = new Map<string, ReferenceBucket>();
    const add = (urls: string[], bucket: ReferenceBucket) => {
      for (const url of urls) if (!wanted.has(url)) wanted.set(url, bucket);
    };
    add(characters, 'character');
    add(objects, 'object');
    add(images, 'image');

    const kept = referenceImages.filter((url) => wanted.has(url));
    const added = Array.from(wanted.keys()).filter(
      (url) => !referenceImages.includes(url),
    );
    const merged = [...kept, ...added];
    const next = merged.slice(0, MAX_EDIT_REFERENCE_IMAGES);
    if (merged.length > next.length) {
      toast(
        t(
          `An edit takes up to ${MAX_EDIT_REFERENCE_IMAGES} reference images, so only the first ${MAX_EDIT_REFERENCE_IMAGES} were added.`,
        ),
      );
    }

    setReferenceImages(next);
    setReferenceBuckets(
      Object.fromEntries(
        next.map((url) => [url, wanted.get(url) ?? 'image'] as const),
      ),
    );
  };

  // ----- submit -----
  const instructions = useMemo(
    () =>
      normalizeVideoEditInstructions(
        cues.map(({ startSeconds, endSeconds, text }) => ({
          startSeconds,
          endSeconds,
          text,
        })),
        duration || 1,
      ),
    [cues, duration],
  );

  const credits = duration
    ? getSeedance25EditCredits(duration, resolution, tier)
    : 0;

  // Each tier renders a different ladder (turbo has no 480p, standard no
  // 1080p), so switching tiers has to carry the resolution somewhere valid.
  const tierResolutions = SEEDANCE_25_EDIT_RESOLUTIONS[
    tier
  ] as EditResolution[];
  const changeTier = (next: Seedance25EditTier) => {
    setTier(next);
    const allowed = SEEDANCE_25_EDIT_RESOLUTIONS[next] as EditResolution[];
    if (!allowed.includes(resolution)) setResolution('720p');
  };

  const mutation = useMutation(
    () =>
      axiosInstance.post('/api/edit-video', {
        videoId: videoId ?? undefined,
        videoUrl: videoId ? undefined : videoUrl,
        instructions,
        resolution,
        tier,
        referenceImages,
      }),
    {
      onSuccess: (response) => {
        toast.success(t('Applying your edits — this takes a few minutes'));
        onSubmitted?.(response.data);
        onClose?.();
      },
      onError: (error) => toastError(error),
    },
  );

  const canSubmit =
    !unsupported &&
    !tooLongForTier &&
    !tooShortForTier &&
    duration > 0 &&
    instructions.length > 0 &&
    !isFreePlan;

  // The Edit Video tool keeps the form when the clip is swapped (the user may
  // have written their edits first). Time ranges belonged to the old clip, so
  // they fall back to whole-clip edits; the text stays.
  const loadedUrlRef = useRef(videoUrl);
  useEffect(() => {
    if (loadedUrlRef.current === videoUrl) return;
    loadedUrlRef.current = videoUrl;
    durationRef.current = 0;
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    setMetadataError(false);
    setCues((prev) =>
      prev.map((cue) => ({ ...cue, startSeconds: null, endSeconds: null })),
    );
  }, [videoUrl]);

  // A model that can't take this clip's length is never left selected.
  useEffect(() => {
    if (duration > 0 && !fitsTier(duration, tier)) {
      const fallback = EDIT_ENGINES.find((engine) =>
        fitsTier(duration, engine.tier),
      );
      if (fallback) changeTier(fallback.tier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const clearInputs = () => {
    const fresh: Cue = {
      id: newId(),
      startSeconds: null,
      endSeconds: null,
      text: '',
    };
    setCues([fresh]);
    setActiveCueId(fresh.id);
    setReferenceImages([]);
    setReferenceBuckets({});
    onClearVideo?.();
  };

  // Keep the playhead in sync without re-rendering on every frame. Re-bound
  // per clip: the tool renders no <video> until one is picked.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => setCurrentTime(video.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [videoUrl]);

  const ticks = useMemo(() => {
    if (!duration) return [];
    const step = tickStep(duration);
    const marks: number[] = [];
    for (let second = 0; second <= wholeSeconds; second += step) {
      marks.push(second);
    }
    return marks;
  }, [duration, wholeSeconds]);

  // ---------- sections ----------
  const playerSection = (
    <div>
      {inline && (
        <SectionLabel
          label={t('Video to edit')}
          hint={t(
            'The edited video keeps the original length, framing, and aspect ratio',
          )}
          aside={
            videoUrl && onReplaceVideo ? (
              <button
                type='button'
                onClick={onReplaceVideo}
                className='flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-slate-800'
              >
                <RefreshIcon className='h-3.5 w-3.5' />
                {t('Replace video')}
              </button>
            ) : undefined
          }
        />
      )}

      {!videoUrl ? (
        sourcePicker
      ) : (
        <>
          <div className='overflow-hidden rounded-xl bg-black ring-1 ring-black/5 dark:ring-white/10'>
            <video
              ref={videoRef}
              src={videoUrl}
              className={
                inline
                  ? 'max-h-72 w-full object-contain'
                  : 'max-h-[46vh] w-full object-contain'
              }
              playsInline
              onLoadedMetadata={handleLoadedMetadata}
              onError={() => setMetadataError(true)}
            />
          </div>

          <div className='mt-3 flex items-center gap-2.5'>
            <button
              type='button'
              onClick={togglePlay}
              className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition-colors hover:bg-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600'
              aria-label={isPlaying ? t('Pause') : t('Play')}
            >
              {isPlaying ? (
                <PauseIcon className='h-4 w-4' />
              ) : (
                <PlayIcon className='ml-0.5 h-4 w-4' />
              )}
            </button>
            <span className='font-mono text-xs tabular-nums text-gray-600 dark:text-slate-300'>
              {currentTime.toFixed(1)}s / {duration ? duration.toFixed(1) : '—'}
              s
            </span>
            <button
              type='button'
              onClick={() => addCue()}
              disabled={
                unsupported || !duration || cues.length >= MAX_EDIT_INSTRUCTIONS
              }
              className='ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-40 disabled:hover:bg-transparent dark:text-primary-400 dark:hover:bg-slate-800'
            >
              <PlusIcon className='h-3.5 w-3.5' />
              {t('Add edit here')}
            </button>
          </div>

          {/* timeline */}
          <div className='mt-3 select-none'>
            <div
              ref={trackRef}
              onClick={handleTrackClick}
              className='relative h-16 w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 dark:border-white/5 dark:bg-slate-800/60'
            >
              {!duration && !metadataError && (
                <div className='absolute inset-0 flex items-center justify-center text-xs text-gray-400 dark:text-slate-500'>
                  <Spinner className='mr-2 h-3.5 w-3.5' />
                  {t('Loading the clip…')}
                </div>
              )}

              {/* cue bands — ranged cues sit in the upper lane, whole-clip
                  cues in a thin lane underneath so the two never overlap */}
              {duration > 0 &&
                cues.map((cue, index) => {
                  const isActive = cue.id === activeCueId;
                  const isGlobal = cue.startSeconds === null;
                  const start = cue.startSeconds ?? 0;
                  const end = cue.endSeconds ?? wholeSeconds;
                  const left = isGlobal ? 0 : (start / wholeSeconds) * 100;
                  const width = isGlobal
                    ? 100
                    : ((end - start) / wholeSeconds) * 100;
                  return (
                    <div
                      key={cue.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveCueId(cue.id);
                        if (!isGlobal) seekTo(start);
                      }}
                      className={clsxm(
                        'absolute rounded',
                        isGlobal
                          ? 'bottom-1 h-4 border border-dashed'
                          : 'top-1 h-9 border-2 border-solid',
                        isActive
                          ? 'z-20 border-primary-500 bg-primary-500/25'
                          : 'z-10 border-primary-300 bg-primary-400/15',
                      )}
                      style={{ left: `${left}%`, width: `${width}%` }}
                    >
                      <span
                        className={clsxm(
                          'absolute left-1 text-[10px] font-semibold',
                          isGlobal ? 'top-0' : 'top-0.5',
                          isActive
                            ? 'text-primary-700 dark:text-primary-200'
                            : 'text-primary-500/70',
                        )}
                      >
                        {index + 1}
                      </span>
                      {!isGlobal && (
                        <>
                          <span
                            onPointerDown={(event) =>
                              startHandleDrag(event, cue.id, 'start')
                            }
                            className='absolute -left-1.5 top-1/2 h-7 w-3 -translate-y-1/2 cursor-ew-resize rounded-sm bg-primary-500 shadow'
                          />
                          <span
                            onPointerDown={(event) =>
                              startHandleDrag(event, cue.id, 'end')
                            }
                            className='absolute -right-1.5 top-1/2 h-7 w-3 -translate-y-1/2 cursor-ew-resize rounded-sm bg-primary-500 shadow'
                          />
                        </>
                      )}
                    </div>
                  );
                })}

              {/* playhead */}
              {duration > 0 && (
                <div
                  className='pointer-events-none absolute inset-y-0 z-30 w-0.5 bg-gray-900 dark:bg-white'
                  style={{
                    left: `${Math.min(100, (currentTime / duration) * 100)}%`,
                  }}
                >
                  <span className='absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-gray-900 dark:bg-white' />
                </div>
              )}
            </div>

            {/* ruler */}
            <div className='relative mt-1 h-4'>
              {ticks.map((second) => (
                <span
                  key={second}
                  className='absolute -translate-x-1/2 text-[10px] tabular-nums text-gray-400 dark:text-slate-500'
                  style={{
                    left: `${Math.min(100, (second / wholeSeconds) * 100)}%`,
                  }}
                >
                  {formatEditSeconds(second)}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const editsSection = (
    <div>
      <SectionLabel
        label={t('Edits')}
        hint={t(
          'Describe each change. Drag the handles on the timeline to set when it applies — ranges snap to whole seconds, the finest timing the model reads.',
        )}
        aside={
          <span className='text-xs text-gray-500 dark:text-slate-500'>
            {cues.length} / {MAX_EDIT_INSTRUCTIONS}
          </span>
        }
      />

      {unsupported ? (
        <div className='rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200'>
          {metadataError
            ? t('This video could not be loaded for editing.')
            : tooShort
              ? t(
                  'AI editing needs a clip of at least 4 seconds. This one is shorter.',
                )
              : t(
                  'AI editing works on clips up to 30 seconds. This one is longer.',
                )}
        </div>
      ) : (
        <>
          <div className='space-y-3'>
            {cues.map((cue, index) => {
              const isActive = cue.id === activeCueId;
              const isGlobal = cue.startSeconds === null;
              return (
                <div
                  key={cue.id}
                  onClick={() => setActiveCueId(cue.id)}
                  className={clsxm(
                    'rounded-xl border bg-white p-3 transition-colors dark:bg-slate-800/40',
                    isActive
                      ? 'border-primary-400 ring-1 ring-primary-400/40 dark:border-primary-500'
                      : 'border-gray-200 hover:border-gray-300 dark:border-white/10',
                  )}
                >
                  <div className='flex items-center gap-2'>
                    <span
                      className={clsxm(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                        isActive
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-200 text-gray-600 dark:bg-slate-700 dark:text-slate-300',
                      )}
                    >
                      {index + 1}
                    </span>
                    <SimpleTooltip
                      title={
                        isGlobal
                          ? duration
                            ? t('Limit this edit to a time range')
                            : undefined
                          : t('Apply this edit to the whole clip')
                      }
                    >
                      <button
                        type='button'
                        onClick={() => toggleCueScope(cue)}
                        disabled={isGlobal && !duration}
                        className='flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-default disabled:hover:bg-gray-100 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'
                      >
                        <ClockIcon className='h-3.5 w-3.5 text-gray-400' />
                        {describeEditRange(cue)}
                      </button>
                    </SimpleTooltip>
                    {!isGlobal && (
                      <>
                        <button
                          type='button'
                          onClick={() =>
                            updateCue(cue.id, {
                              startSeconds: clampSecond(
                                currentTime,
                                0,
                                (cue.endSeconds ?? wholeSeconds) - 1,
                              ),
                            })
                          }
                          className='rounded-md px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-700'
                        >
                          {t('start here')}
                        </button>
                        <button
                          type='button'
                          onClick={() =>
                            updateCue(cue.id, {
                              endSeconds: clampSecond(
                                currentTime,
                                (cue.startSeconds ?? 0) + 1,
                                wholeSeconds,
                              ),
                            })
                          }
                          className='rounded-md px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-700'
                        >
                          {t('end here')}
                        </button>
                      </>
                    )}
                    <button
                      type='button'
                      onClick={(event) => {
                        event.stopPropagation();
                        removeCue(cue.id);
                      }}
                      className='ml-auto rounded-md p-1 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-slate-500 dark:hover:bg-red-500/10'
                      aria-label={t('Remove')}
                    >
                      <TrashIcon className='h-4 w-4' />
                    </button>
                  </div>
                  <ReactTextareaAutosize
                    value={cue.text}
                    maxLength={MAX_EDIT_INSTRUCTION_LENGTH}
                    onChange={(event) =>
                      updateCue(cue.id, { text: event.target.value })
                    }
                    onPaste={(event) => handleCuePaste(cue, event)}
                    onFocus={() => setActiveCueId(cue.id)}
                    minRows={2}
                    maxRows={6}
                    placeholder={t(
                      'e.g. remove the person walking in the background',
                    )}
                    className='mt-2.5 block w-full resize-none rounded border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm'
                  />
                  {/* The cap cuts typing and pasting off silently, so show it
                      before it's reached. */}
                  {cue.text.length >= MAX_EDIT_INSTRUCTION_LENGTH - 80 && (
                    <p
                      className={clsxm(
                        'mt-1 text-right text-[11px] tabular-nums',
                        cue.text.length >= MAX_EDIT_INSTRUCTION_LENGTH
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-400 dark:text-slate-500',
                      )}
                    >
                      {cue.text.length}/{MAX_EDIT_INSTRUCTION_LENGTH}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {cues.length < MAX_EDIT_INSTRUCTIONS && (
            <button
              type='button'
              onClick={() => addCue()}
              disabled={!duration}
              className='mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-2.5 text-sm font-medium text-gray-600 transition-all hover:border-primary-400 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:border-primary-500'
            >
              <PlusIcon className='h-4 w-4' />
              {t('Add an edit')}
            </button>
          )}

          <p className='mb-2 mt-4 text-xs font-medium text-gray-500 dark:text-slate-400'>
            {t('Quick edits')}
          </p>
          <div className='flex flex-wrap gap-1.5'>
            {QUICK_EDITS.map((quick) => (
              <button
                key={quick.label}
                type='button'
                onClick={() => applyQuickEdit(quick)}
                className='rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600 transition-colors hover:border-primary-400 hover:text-primary-600 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300'
              >
                {t(quick.label)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const referencesSection = (
    <div>
      <SectionLabel
        label={`${t('References')} ${t('(optional)')}`}
        hint={t(
          'Refer to them in an edit by tag, e.g. "replace the jacket with @Image1".',
        )}
        aside={
          referenceImages.length > 0 ? (
            <span className='flex items-center gap-2'>
              <button
                type='button'
                onClick={openLibrary}
                className='flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-slate-800'
              >
                <CollectionIcon className='h-3.5 w-3.5' />
                {t('Library')}
              </button>
              <span className='text-xs text-gray-500 dark:text-slate-500'>
                {referenceImages.length} / {MAX_EDIT_REFERENCE_IMAGES}
              </span>
            </span>
          ) : undefined
        }
      />
      {referenceImages.length === 0 ? (
        <div className='grid grid-cols-2 gap-2'>
          <label className='flex w-full cursor-pointer items-center gap-3 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-3.5 transition-all hover:border-primary-400 hover:bg-gray-100 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-primary-500 dark:hover:bg-slate-800'>
            <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-gray-500 ring-1 ring-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600'>
              {uploadingReference ? (
                <Spinner className='h-4 w-4' />
              ) : (
                <PhotographIcon className='h-5 w-5' />
              )}
            </span>
            <span className='min-w-0'>
              <span className='block text-sm font-medium text-gray-600 dark:text-slate-300'>
                {t('Upload images')}
              </span>
              <span className='block text-xs text-gray-400 dark:text-slate-500'>
                {t('From your device')}
              </span>
            </span>
            <input
              type='file'
              accept='image/*'
              multiple
              className='hidden'
              onChange={(event) => {
                handleReferenceUpload(event.target.files);
                event.target.value = '';
              }}
            />
          </label>
          <button
            type='button'
            onClick={openLibrary}
            className='flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-3.5 text-left transition-all hover:border-primary-400 hover:bg-gray-100 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-primary-500 dark:hover:bg-slate-800'
          >
            <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-gray-500 ring-1 ring-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600'>
              <CollectionIcon className='h-5 w-5' />
            </span>
            <span className='min-w-0'>
              <span className='block text-sm font-medium text-gray-600 dark:text-slate-300'>
                {t('From library')}
              </span>
              <span className='block text-xs text-gray-400 dark:text-slate-500'>
                {t('Uploads & generated')}
              </span>
            </span>
          </button>
        </div>
      ) : (
        <div className='grid grid-cols-4 gap-2'>
          {referenceImages.map((url, index) => (
            <div
              key={url}
              className='group relative aspect-square overflow-hidden rounded-lg ring-1 ring-gray-200 dark:ring-white/10'
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={url}
                alt={`Reference ${index + 1}`}
                className='h-full w-full object-cover'
              />
              <span className='absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] font-medium text-white backdrop-blur-sm'>
                @Image{index + 1}
              </span>
              <button
                type='button'
                onClick={() => {
                  setReferenceImages((prev) =>
                    prev.filter((item) => item !== url),
                  );
                  setReferenceBuckets((prev) => {
                    const next = { ...prev };
                    delete next[url];
                    return next;
                  });
                }}
                className='absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/80'
                aria-label={t('Remove')}
              >
                <XIcon className='h-3 w-3' />
              </button>
            </div>
          ))}
          {referenceImages.length < MAX_EDIT_REFERENCE_IMAGES && (
            <label className='flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400 transition-all hover:border-primary-400 hover:text-primary-500 dark:border-slate-600 dark:bg-slate-800/60'>
              {uploadingReference ? (
                <Spinner className='h-4 w-4' />
              ) : (
                <PlusIcon className='h-5 w-5' />
              )}
              <input
                type='file'
                accept='image/*'
                multiple
                className='hidden'
                onChange={(event) => {
                  handleReferenceUpload(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
          )}
        </div>
      )}
      {/* Rendered inside this editor's tree, not beside it: in the gallery the
          editor itself sits in a Modal, and a dialog opened as a sibling would
          read every click inside it as an outside click and close both. */}
      {libraryMounted && (
        <SceneModal
          isOpen={showLibrary}
          onClose={() => setShowLibrary(false)}
          initialTab='all'
          hiddenTabs={[...LIBRARY_ONLY_TABS]}
          showCustomActors
          selectedCharacters={referencesInBucket('character')}
          selectedObjects={referencesInBucket('object')}
          selectedImages={referencesInBucket('image')}
          onLibrarySelectionApply={applyLibrarySelection}
        />
      )}
    </div>
  );

  const modelSection = (
    <div>
      <SectionLabel
        label={t('Model')}
        hint={t(
          'The models trade quality, speed, price, and the clip lengths they accept',
        )}
      />
      <fieldset className='space-y-3'>
        {EDIT_ENGINES.map((engine) => {
          const Icon = engineIcon(engine.catalogTitle);
          const { maxSeconds } = getVideoEditTierLimits(engine.tier);
          const resolutions = SEEDANCE_25_EDIT_RESOLUTIONS[engine.tier];
          const fits = !duration || fitsTier(duration, engine.tier);
          return (
            <label
              key={engine.tier}
              className={clsxm(
                'relative flex items-start pl-1',
                fits ? 'cursor-pointer' : 'cursor-not-allowed',
              )}
            >
              <span className='flex h-6 items-center'>
                <input
                  type='radio'
                  name='videoEditModel'
                  value={engine.tier}
                  checked={tier === engine.tier}
                  onChange={() => changeTier(engine.tier)}
                  disabled={!fits}
                  className='h-4 w-4 cursor-pointer border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:opacity-50'
                />
              </span>
              <span className='ml-3 text-sm leading-6'>
                <span
                  className={clsxm(
                    'flex items-center font-medium',
                    fits
                      ? 'text-gray-900 dark:text-slate-400'
                      : 'text-gray-400 dark:text-slate-600',
                  )}
                >
                  {Icon &&
                    React.createElement(Icon, {
                      className: clsxm('mr-2 h-4 w-4', !fits && 'opacity-50'),
                    })}
                  {engine.label}
                </span>
                <span className='block text-xs leading-5 text-gray-500 dark:text-slate-500'>
                  {fits
                    ? `${t(engine.blurb)} · ${t('up to')} ${maxSeconds}s · ${resolutions[0]}–${resolutions[resolutions.length - 1]}`
                    : t(`Takes clips up to ${maxSeconds} seconds`)}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>
    </div>
  );

  const submitButton = (
    <Button
      onClick={() => mutation.mutate()}
      disabled={!canSubmit}
      isLoading={mutation.isLoading}
      variant={inline ? 'dark' : 'primary'}
      className={clsxm(
        'disabled:opacity-50',
        inline && 'xl:px-6 xl:py-3 xl:text-base',
      )}
    >
      {!inline && <SparklesIcon className='mr-2 h-4 w-4' />}
      {t('Apply edits')}
      {inline && credits > 0 && !unsupported
        ? ` (${credits} ${t('credits')})`
        : ''}
    </Button>
  );

  const resolutionPicker = (
    <ResolutionSelector
      value={resolution}
      onChange={(value) => setResolution(value as EditResolution)}
      options={tierResolutions}
    />
  );

  // ---------- inline: the Edit Video tool, laid out like the generator ----------
  if (inline) {
    return (
      <div className='relative flex h-full flex-col'>
        <div className='space-y-6 px-4 py-5 pb-16 sm:p-6 sm:pt-0'>
          {playerSection}
          {editsSection}
          {referencesSection}
          {modelSection}
        </div>

        <footer className='bottom-0 right-0 z-10 w-full space-y-3 border-b border-t border-gray-200 bg-gray-100 pb-4 pl-6 pr-4 pt-4 dark:border-white/10 dark:bg-slate-800 sm:sticky'>
          <div className='flex flex-wrap items-center gap-2'>
            {resolutionPicker}
            {duration > 0 && !unsupported && (
              <SimpleTooltip
                title={t(
                  'The edited video keeps the original length, framing, and aspect ratio',
                )}
                side='bottom'
              >
                <span className='flex items-center gap-1.5 rounded-xl border bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm dark:border-white/5 dark:bg-slate-700/50 dark:text-gray-200'>
                  <ClockIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
                  {Math.round(duration)}s
                </span>
              </SimpleTooltip>
            )}
          </div>
          <div className='flex items-center justify-between gap-3'>
            {isFreePlan ? (
              <div className='flex flex-col gap-1.5'>
                <ButtonLink href='/settings/billing' variant='dark'>
                  {t('Upgrade plan')}
                </ButtonLink>
                <span className='text-xs text-amber-600 dark:text-amber-400'>
                  {t('AI video editing is available on paid plans.')}
                </span>
              </div>
            ) : (
              submitButton
            )}
            <button
              type='button'
              onClick={clearInputs}
              className='flex items-center gap-1 self-center text-xs text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'
            >
              <XIcon className='h-3.5 w-3.5' />
              <span>{t('Clear inputs')}</span>
            </button>
          </div>
        </footer>
      </div>
    );
  }

  // ---------- modal: the gallery's "Edit with AI" dialog ----------
  return (
    <div className='flex max-h-[88vh] flex-col'>
      <header className='shrink-0 border-b border-gray-200 px-5 py-4 dark:border-slate-700'>
        <h2 className='flex items-center text-lg font-semibold text-gray-900 dark:text-white'>
          <SparklesIcon className='mr-2 h-5 w-5 text-primary-500' />
          {t('Edit video with AI')}
        </h2>
        <p className='mt-1 text-sm text-gray-500 dark:text-slate-400'>
          {t(
            'Point at a moment, describe what should change. Everything else stays exactly as it is.',
          )}
        </p>
      </header>

      <div className='grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-5 lg:grid-cols-5'>
        <div className='space-y-6 lg:col-span-3'>
          {playerSection}
          {referencesSection}
        </div>
        <div className='space-y-6 lg:col-span-2'>
          {editsSection}
          {modelSection}
        </div>
      </div>

      <footer className='shrink-0 border-t border-gray-200 px-5 py-4 dark:border-slate-700'>
        <div className='flex flex-wrap items-center gap-3'>
          {resolutionPicker}
          <p className='text-xs text-gray-500 dark:text-slate-400'>
            {t('Keeps the original length, framing, and aspect ratio')}
          </p>
          <div className='ml-auto flex items-center gap-3'>
            {credits > 0 && !unsupported && (
              <span className='text-sm text-gray-600 dark:text-slate-300'>
                {credits} {t('credits')}
              </span>
            )}
            <Button variant='outline' onClick={onClose}>
              {t('Cancel')}
            </Button>
            {submitButton}
          </div>
        </div>
        {isFreePlan && (
          <p className='mt-2 text-xs text-amber-600 dark:text-amber-400'>
            {t('AI video editing is available on paid plans.')}
          </p>
        )}
      </footer>
    </div>
  );
}
